import moment from "moment-timezone";

export const DEFAULT_CURRENCY_CODE = "INR";

const CURRENCY_CONFIG = {
  DEFAULT: { locale: "en-IN", currency: "INR", decimals: 2 },
  INR: { locale: "en-IN", currency: "INR", decimals: 2 },
  INR_3: { locale: "en-IN", currency: "INR", decimals: 3 },
  MYR: { locale: "en-MY", currency: "MYR", decimals: 2 },
  THB: { locale: "th-TH", currency: "THB", decimals: 2 },
  VND: { locale: "vi-VN", currency: "VND", decimals: 0 },
  PHP: { locale: "en-PH", currency: "PHP", decimals: 2 },
  IDR: { locale: "id-ID", currency: "IDR", decimals: 0 },
  USD: { locale: "en-US", currency: "USD", decimals: 2 },
};

export const convertUTCToTimezone = (
  utcDate,
  tz,
  format = "YYYY-MM-DD HH:mm:ss",
) => {
  if (!utcDate) {
    return "";
  }
  const resolved_tz = "Asia/Kuala_Lumpur";
  return moment.utc(utcDate).tz(resolved_tz).format(format);
};

export const formatCurrencyCountry = (amount, code, rounded = false) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return null;
  }

  const resolvedKey = code != null ? code : DEFAULT_CURRENCY_CODE;

  let codeConfig = CURRENCY_CONFIG[resolvedKey];
  if (!codeConfig) {
    codeConfig = CURRENCY_CONFIG.DEFAULT;
  }

  const { locale, currency, decimals } = codeConfig;
  let n = Number(amount);
  if (rounded) {
    n = Math.round(n);
  }
  let formatedCurrency = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

  formatedCurrency = formatedCurrency.replace(/([.,]00)(?!\d)/, "");

  const parts = splitFormattedCurrency(formatedCurrency);

  return {
    formatedAmount: formatedCurrency,
    ...parts,
  };
};

export function splitFormattedCurrency(formattedCurrency) {
  const fallback = () => ({
    currency: "",
    mainAmount: "",
    decimalAmount: null,
    separator: null,
    is_currency_at_start: true,
  });

  if (formattedCurrency == null || typeof formattedCurrency !== "string") {
    return fallback();
  }

  const s = formattedCurrency.replace(/\u00A0/g, " ").trim();
  if (!s) return fallback();

  // First and last digit index
  const firstDigitIdx = s.split("").findIndex((ch) => /\d/.test(ch));
  let lastDigitIdx = -1;
  for (let i = s.length - 1; i >= 0; i--) {
    if (/\d/.test(s[i])) {
      lastDigitIdx = i;
      break;
    }
  }

  if (firstDigitIdx === -1 || lastDigitIdx === -1) {
    return { ...fallback(), currency: s };
  }

  // Split currency symbol from numeric core
  let currency = "";
  let is_currency_at_start = true;
  let core;

  if (firstDigitIdx > 0) {
    // Currency at start e.g. "$ 1,234.56"
    currency = s.slice(0, firstDigitIdx).trim();
    core = s.slice(firstDigitIdx, lastDigitIdx + 1);
  } else {
    // Currency at end e.g. "1.234,56 €"
    core = s.slice(0, lastDigitIdx + 1);
    const tail = s.slice(lastDigitIdx + 1).trim();
    if (tail) {
      currency = tail;
      is_currency_at_start = false;
    }
  }

  // Strip thousands separators (spaces and apostrophes)
  core = core.replace(/[\s']/g, "");

  // Split core into mainAmount + decimalAmount
  let mainAmount = core;
  let decimalAmount = null;

  const decimalSep = detectDecimalSeparator(core);

  let separator = null;
  if (decimalSep) {
    const cutIdx = core.lastIndexOf(decimalSep);
    const rhs = core.slice(cutIdx + 1).replace(/\D/g, "");
    if (rhs.length > 0) {
      mainAmount = core.slice(0, cutIdx);
      decimalAmount = rhs;
      separator = decimalSep;
    }
  }

  return {
    currency,
    mainAmount,
    decimalAmount,
    separator,
    is_currency_at_start,
  };
}

function detectDecimalSeparator(core) {
  const dotCount = (core.match(/\./g) || []).length;
  const commaCount = (core.match(/,/g) || []).length;
  const lastDot = core.lastIndexOf(".");
  const lastComma = core.lastIndexOf(",");

  // Both present → whichever comes last is decimal sep
  // e.g. "1,234.56" → dot  |  "1.234,56" → comma
  if (lastDot !== -1 && lastComma !== -1) {
    return lastDot > lastComma ? "." : ",";
  }

  // Only one present → decimal only if appears once with 1–2 digits after
  const sep = lastDot !== -1 ? "." : lastComma !== -1 ? "," : null;
  const count = sep === "." ? dotCount : commaCount;

  if (!sep || count !== 1) return null;

  const afterSep = core.slice(core.lastIndexOf(sep) + 1).replace(/\D/g, "");
  return afterSep.length >= 1 && afterSep.length <= 2 ? sep : null;
}
